// @flow

import React from 'react'
import { useIntl } from 'gatsby-plugin-intl'
import YouTube from 'react-youtube'
import { ParallaxProvider } from 'react-scroll-parallax'
import { applyStylingHoc, type StyleOuterProps, type StyleProps } from '@fcse/tsbob/dist/lib/styling'
import { Container } from '../../components/basic/Grid'
import VideoCookiesWrapper from '../VideoCookiesWrapper'
import ScrollAnimation from 'react-animate-on-scroll'
import CaseTeasers from '../../components/CaseStudy/CaseTeasers'
import CaseHeroTeaser from './CaseHeroTeaser'
import CaseFacts from './CaseFacts'
import CaseWelcome from './CaseWelcome'
import CaseLogosSlider from './CaseLogosSlider'
import CaseInfo from './CaseInfo'
import CaseStats from './CaseStats'
import CaseFunctionalities from './CaseFunctionalities'
import CaseRoadmap from './CaseRoadmap'
import CaseBrand from './CaseBrand'
import CaseFeedback from './CaseFeedback'
import CaseReviews from './CaseReviews'
import CaseActionTeaser from './CaseActionTeaser'
import CaseTeaser from './CaseTeaser'

import baseStyles from './CaseStudy.scss'
import messages from './CaseStudy.lang'

type Props = {
  ...StyleProps,
  data: Object
}

const CaseStudy = ({ styles, data }: Props) => {
  const { formatMessage } = useIntl()

  return (
    <div className={styles.root}>
      <ParallaxProvider>
        <CaseHeroTeaser
          title={formatMessage(data.heroTeaser.title)}
          description={formatMessage(data.heroTeaser.description)}
          appstore={data.heroTeaser.appstore}
          titleLink={data.link}
          name={data.name}
          imageDesktop={data.heroTeaser.bg.imageDesktop}
          caseImg={data.heroTeaser.img}
          styles={styles.heroTeaser}
          appstoreImg={data.heroTeaser.appstoreImg}
          googleplayImg={data.heroTeaser.googleplayImg}
        />
        {data.facts && <CaseFacts facts={data.facts} />}
        {data.welcome && (
          <CaseWelcome
            logo={data.welcome.logo}
            logoMobile={data.welcome.logoMobile}
            text={data.welcome.text}
            title={data.welcome.title}
            img={data.welcome.img}
            styles={styles.welcome}
          />
        )}
        {data.logosSlider && (
          <CaseLogosSlider title={data.logosSlider.title} list={data.logosSlider.list} styles={styles.logosSlider} />
        )}
        {data.info && (
          <CaseInfo title={data.info.title} list={data.info.list} img={data.info.img} styles={styles.info} />
        )}
        {data.stats && <CaseStats className={styles.stats} stats={data.stats} />}
        {data.functionalities && (
          <>
            <Container className={styles.container} fluid>
              <h2 className={styles.title}>{formatMessage(data.functionalitiesTitle)}</h2>
            </Container>
            <CaseFunctionalities
              functionalities={data.functionalities}
              styles={styles.functionalities}
              inversed={data.functionalitiesListInversed}
            />
          </>
        )}
        {data.roadmap && <CaseRoadmap roadmap={data.roadmap} title={data.roadmapTitle} />}
        {data.solution && (
          <>
            <Container className={styles.container} fluid>
              <h2 className={styles.title}>{formatMessage(data.solution.title)}</h2>
            </Container>
            <CaseTeaser
              title={data.solution.solutionTextTitle}
              richText={data.solution.text}
              img={data.solution.img}
              styles={styles.solution}
              reversed
              imgWide
            />
          </>
        )}
        {data.brand && (
          <>
            <Container className={styles.container} fluid>
              <h2 className={styles.title}>{formatMessage(data.brand.title)}</h2>
            </Container>
            <CaseBrand brand={data.brand} styles={styles.brand} />
          </>
        )}
        {data.videoId && (
          <Container className={styles.container} fluid>
            <h2 className={styles.title}>
              {data.videoTitle ? formatMessage(data.videoTitle) : formatMessage(messages.knowTitle)}
            </h2>
            <VideoCookiesWrapper styles={styles.videoWrapper}>
              <YouTube videoId={data.videoId} className={styles.video} containerClassName={styles.videoHolder} />
            </VideoCookiesWrapper>
          </Container>
        )}
        {data.feedback && <CaseFeedback list={data.feedback} />}
        {data.reviews && (
          <>
            <Container className={styles.container} fluid>
              <h2 className={styles.title}>{formatMessage(messages.reviewesTitle)}</h2>
            </Container>
            <CaseReviews reviews={data.reviews} />
          </>
        )}
        {data.link && (
          <CaseActionTeaser link={data.link} img={data.check.img} title={data.check.title} text={data.check.text} />
        )}
      </ParallaxProvider>
      <ScrollAnimation animateIn="fadeInUp" animatePreScroll={false} animateOnce>
        <CaseTeasers
          list={['adac', 'mitsubishi', 'smm', 'dertouristik', 'lhh', 'plattenplaner', 'aldiana', 'etravel', 'weekend', 'bedfinder', 'dertouristik']}
          allButton
        />
      </ScrollAnimation>
    </div>
  )
}
export default applyStylingHoc(baseStyles)<$Diff<Props, StyleProps> & StyleOuterProps, Function>(CaseStudy)
